// src/components/SPanel/Home/MoreWidgets.jsx
//
// Încă câteva widget-uri pentru pagina principală: cele de pe vechea pagină
// (lecția următoare, notificările, examenele) și câteva noi (de recapitulat,
// obiectivul zilei, încercările la examenul teoretic).

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { CountUp, Head, Skeleton, Tile } from "./HomeTile";
import { useAppeared, useCountUp, useElementSize } from "./homeHooks";
import { formatShortDay, niceMax } from "./homeActivity";
import { formatCountdown, formatDayMonth, formatDriven, formatTime, roCount } from "./homeLessons";
import { openPopup } from "../../Common/popupUIStore";
import { ticketNumber } from "../../../utils/practiceStatsSummary";
import {
   formatNotificationTime,
   notificationBody,
   notificationCreatedAt,
   notificationGroup,
   notificationId,
   notificationTitle,
} from "../../../utils/notificationsFormat";

import calendarIcon from "../../../assets/svg/mdi--calendar-outline.svg";
import bellIcon from "../../../assets/svg/mingcute--notification-line.svg";
import bookIcon from "../../../assets/svg/material-symbols--book-outline.svg";
import examIcon from "../../../assets/svg/mdi--book-clock-outline.svg";
import chartIcon from "../../../assets/svg/mdi--chart-line.svg";

function formatShortDate(value) {
   const d = value ? new Date(value) : null;
   if (!d || Number.isNaN(d.getTime())) return "";
   return d.toLocaleDateString("ro-RO", { day: "numeric", month: "short" }).replaceAll(".", "");
}

/* ================= lecția următoare ================= */

export function NextLessonWidget({ size, data }) {
   const { track, now } = data;
   const next = track.next;

   if (!next) {
      return (
         <Tile id="next" size={size} onClick={() => openPopup("sAddProg")} label="Programează o lecție">
            <Head icon={calendarIcon} title="Lecția următoare" />
            <div className="wd-next__empty">
               <strong>Nicio lecție programată</strong>
               <span className="wd-next__cta">Programează →</span>
            </div>
         </Tile>
      );
   }

   const when = formatDayMonth(next.start);
   const live = next.state === "live";
   const countdown = (
      <span className={`wd-countdown${live ? " is-live" : ""}`}>
         {formatCountdown(next.start, next.end, now)}
      </span>
   );
   const open = () => openPopup("eventInfo", { event: next.event });

   if (size === "1x1") {
      return (
         <Tile id="next" size={size} onClick={open} label={`Lecția următoare: ${when.day} ${when.month}, ${formatTime(next.start)}`}>
            <Head icon={calendarIcon} title="Lecția următoare" />
            {countdown}
            <div className="wd-next__main">
               <span className="wd-next__day">
                  {when.day}
                  <small>{when.month}</small>
               </span>
               <span className="wd-next__info">
                  <span className="wd-next__weekday wd-roomy">{when.weekday}</span>
                  <span className="wd-next__time">
                     {formatTime(next.start)}
                     <span className="wd-roomy"> – {formatTime(next.end)}</span>
                  </span>
                  <span className="wd-next__number wd-roomy">
                     Lecția {String(next.number).padStart(2, "0")}
                  </span>
               </span>
            </div>
            <div className="wd-tags wd-roomy wd-roomy--flex">
               <span className="wd-tag">{formatDriven(Math.round((next.end - next.start) / 60000))}</span>
               {next.plate && <span className="wd-tag">{next.plate}</span>}
               {next.gearbox && <span className="wd-tag wd-tag--cap">{next.gearbox}</span>}
            </div>
         </Tile>
      );
   }

   return (
      <Tile id="next" size={size} onClick={open} label="Lecția următoare">
         <Head icon={calendarIcon} title="Lecția următoare" extra={countdown} />
         <div className="wd-next__main">
            <span className="wd-next__day">
               {when.day}
               <small>{when.month}</small>
            </span>
            <span className="wd-next__info">
               <span className="wd-next__weekday">{when.weekday}</span>
               <span className="wd-next__time">
                  {formatTime(next.start)} – {formatTime(next.end)}
               </span>
               <span className="wd-next__number">Lecția {String(next.number).padStart(2, "0")}</span>
            </span>
         </div>
         <div className="wd-tags">
            <span className="wd-tag">{formatDriven(Math.round((next.end - next.start) / 60000))}</span>
            {next.plate && <span className="wd-tag">{next.plate}</span>}
            {next.gearbox && <span className="wd-tag wd-tag--cap">{next.gearbox}</span>}
            {next.sector && <span className="wd-tag wd-tag--cap">{next.sector}</span>}
         </div>
      </Tile>
   );
}

/* ================= notificări ================= */

export function NotificationsWidget({ size, data }) {
   const { loaded, unread, items } = data.notifications;
   const openAll = () => openPopup("notifications");

   if (size === "1x1") {
      return (
         <Tile id="notifications" size={size} onClick={openAll} label={`Notificări: ${unread} noi`}>
            <Head icon={bellIcon} title="Notificări" />
            <div className="wd-big">
               <span className="wd-big__num">
                  <CountUp value={unread} />
               </span>
            </div>
            <span className="wd-caption">{unread === 1 ? "notificare nouă" : "notificări noi"}</span>
         </Tile>
      );
   }

   const shown = size === "2x2" ? items : items.slice(0, 2);
   return (
      <Tile id="notifications" size={size}>
         <Head
            icon={bellIcon}
            title="Notificări"
            extra={unread > 0 ? <span className="wd-count">{unread > 99 ? "99+" : unread}</span> : null}
         />
         {!loaded ? (
            <Skeleton />
         ) : shown.length === 0 ? (
            <button type="button" className="wd-empty" onClick={openAll}>
               <strong>Nimic nou</strong>
               <span>Te anunțăm aici când apare ceva.</span>
            </button>
         ) : (
            <ul className={`wd-notes${size === "2x2" ? " wd-notes--full" : ""}`}>
               {shown.map((item, index) => (
                  <li key={notificationId(item) ?? index}>
                     <button
                        type="button"
                        className={`wd-notes__item wd-notes__item--${notificationGroup(item).toLowerCase()}`}
                        onClick={openAll}
                     >
                        <span className="wd-notes__dot" aria-hidden="true" />
                        <span className="wd-notes__text">
                           <strong>{notificationTitle(item)}</strong>
                           {size === "2x2" && <span>{notificationBody(item)}</span>}
                        </span>
                        <time className="wd-notes__time">
                           {formatNotificationTime(notificationCreatedAt(item))}
                        </time>
                     </button>
                  </li>
               ))}
            </ul>
         )}
      </Tile>
   );
}

/* ================= examene ================= */

function theoryState(theory) {
   const last = theory.last;
   if (!theory.loaded) return { tone: "none", text: "…", sub: "" };
   if (!last) return { tone: "none", text: "Nesusținut", sub: "Testul de la școală" };
   if (last.inProgress) return { tone: "live", text: "În desfășurare", sub: formatShortDate(last.at) };
   return {
      tone: last.passed ? "ok" : "bad",
      text: last.passed ? "Admis" : "Respins",
      score: `${last.score}/${last.total}`,
      sub: `${formatShortDate(last.at)} · ${roCount(theory.attempts, "încercare", "încercări")}`,
   };
}

function practicalState(practical) {
   if (!practical) return { tone: "none", text: "Neprogramat", sub: "Alegi data când primești acces" };
   const status = String(practical.cardItem?.statusLabel || "").trim() || "Programat";
   const tone =
      status === "Admis" ? "ok" : ["Respins", "Absent", "Anulat"].includes(status) ? "bad" : "live";
   return {
      tone,
      text: status,
      sub: `${formatDayMonth(practical.start).weekday}, ${formatShortDate(practical.start)} · ${formatTime(practical.start)}`,
   };
}

export function ExamsWidget({ size, data }) {
   const theory = theoryState(data.theory);

   if (size === "1x1") {
      return (
         <Tile id="exams" size={size} to="/student/exam" label={`Examen teoretic: ${theory.text}`}>
            <Head icon={examIcon} title="Ex. teoretic" />
            <span className={`wd-status wd-status--${theory.tone} wd-exam__status`}>{theory.text}</span>
            <div className="wd-big">
               <span className="wd-big__num">{theory.score || "—"}</span>
            </div>
            <span className="wd-caption">{theory.sub}</span>
         </Tile>
      );
   }

   const practical = practicalState(data.practical);
   return (
      <Tile id="exams" size={size}>
         <Head icon={examIcon} title="Examene" />
         <div className="wd-rows">
            <Link to="/student/exam" className="wd-row">
               <span className="wd-row__icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                     <path d="M5 4h11l3 3v13H5zm2 2v12h10V8h-2V6zm2 4h6v2H9zm0 4h6v2H9z" />
                  </svg>
               </span>
               <span className="wd-row__text">
                  <strong>Teoretic</strong>
                  <span>{theory.sub}</span>
               </span>
               <span className={`wd-status wd-status--${theory.tone}`}>
                  {theory.score ? `${theory.text} · ${theory.score}` : theory.text}
               </span>
            </Link>
            <button type="button" className="wd-row" onClick={() => openPopup("practicalExamBooking")}>
               <span className="wd-row__icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                     <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11v7h-2v-2H7v2H5zm2.1-1h9.8l-1-3H8.1zM7.5 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2m9 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
                  </svg>
               </span>
               <span className="wd-row__text">
                  <strong>Practic</strong>
                  <span>{practical.sub}</span>
               </span>
               <span className={`wd-status wd-status--${practical.tone}`}>{practical.text}</span>
            </button>
         </div>
      </Tile>
   );
}

/* ================= de recapitulat ================= */

export function MistakesWidget({ size, data }) {
   const { practice } = data;
   if (!practice) {
      return (
         <Tile id="mistakes" size={size}>
            <Head icon={bookIcon} title="De recapitulat" />
            <Skeleton />
         </Tile>
      );
   }
   // întrebări „roșii”: răspunse, dar niciodată corect
   const red = Number(practice.summary?.red) || 0;
   const worst = (practice.tickets || [])
      .filter((row) => Number(row.red) > 0)
      .sort((a, b) => b.red - a.red)
      .slice(0, 3);

   const big = (
      <>
         <div className="wd-big">
            <span className="wd-big__num">
               <CountUp value={red} />
            </span>
         </div>
         <span className="wd-caption">
            {red === 0 ? "nicio întrebare greșită" : red === 1 ? "întrebare greșită" : "întrebări greșite"}
         </span>
      </>
   );

   if (size === "1x1") {
      return (
         <Tile id="mistakes" size={size} to="/student/test" label={`De recapitulat: ${red} întrebări greșite`}>
            <Head icon={bookIcon} title="De recapitulat" />
            {big}
         </Tile>
      );
   }

   return (
      <Tile id="mistakes" size={size}>
         <Head icon={bookIcon} title="De recapitulat" action={{ to: "/student/test", label: "Exersează" }} />
         <div className="wd-split">
            <div className="wd-split__main">{big}</div>
            {worst.length > 0 ? (
               <ol className="wd-worst">
                  {worst.map((row) => (
                     <li key={row.ticketId}>
                        <span>Biletul {ticketNumber(row.ticketId)}</span>
                        <strong>{roCount(row.red, "greșeală", "greșeli")}</strong>
                     </li>
                  ))}
               </ol>
            ) : (
               <p className="wd-caption">Niciun bilet cu greșeli. Bravo!</p>
            )}
         </div>
      </Tile>
   );
}

/* ================= obiectivul zilei ================= */

const GOALS = [20, 30, 50, 80, 100];
const DEFAULT_GOAL = 50;
const goalKey = (userId) => `home.goal.v1:${userId ?? "anon"}`;

function readGoal(userId) {
   try {
      const value = Number(localStorage.getItem(goalKey(userId)));
      return GOALS.includes(value) ? value : DEFAULT_GOAL;
   } catch {
      return DEFAULT_GOAL;
   }
}

function GoalRing({ value, goal, appeared }) {
   const R = 52;
   const C = 2 * Math.PI * R;
   const shown = useCountUp(value, 1100);
   const fill = appeared ? Math.min(1, value / goal) : 0;
   return (
      <div className={`wd-ring wd-goal__ring${value >= goal ? " is-done" : ""}`} role="img" aria-label={`Azi ${value} din ${goal}`}>
         <svg viewBox="0 0 120 120">
            <circle className="wd-ring__track" cx="60" cy="60" r={R} />
            <circle
               className="wd-goal__fill"
               cx="60"
               cy="60"
               r={R}
               strokeDasharray={C}
               strokeDashoffset={C * (1 - fill)}
            />
         </svg>
         <span className="wd-ring__label">
            <strong>{shown}</strong>
            <small>din {goal}</small>
         </span>
      </div>
   );
}

export function GoalWidget({ size, data }) {
   const userId = data.user?.id ?? null;
   const [goal, setGoal] = useState(() => readGoal(userId));
   useEffect(() => setGoal(readGoal(userId)), [userId]);
   const appeared = useAppeared(300);
   const days = data.activity.days;
   const today = days.length ? days[days.length - 1].answers : 0;
   const week = days.slice(-7);
   const reached = week.filter((day) => day.answers >= goal).length;

   const changeGoal = (event) => {
      event.stopPropagation();
      const next = GOALS[(GOALS.indexOf(goal) + 1) % GOALS.length];
      setGoal(next);
      try {
         localStorage.setItem(goalKey(userId), String(next));
      } catch {
         // fără stocare: obiectivul ține până la reîncărcare
      }
   };

   const goalButton = (
      <button type="button" className="wd-goal__change" onClick={changeGoal} title="Schimbă obiectivul">
         Obiectiv {goal} ↻
      </button>
   );

   if (!data.activity.loaded) {
      return (
         <Tile id="goal" size={size}>
            <Head icon={chartIcon} title="Azi" />
            <Skeleton />
         </Tile>
      );
   }

   if (size === "1x1") {
      return (
         <Tile id="goal" size={size}>
            <Head icon={chartIcon} title="Azi" />
            <div className="wd-center">
               <GoalRing value={today} goal={goal} appeared={appeared} />
            </div>
            {goalButton}
         </Tile>
      );
   }

   const max = Math.max(goal, ...week.map((day) => day.answers));
   return (
      <Tile id="goal" size={size}>
         <Head icon={chartIcon} title="Obiectivul zilei" extra={goalButton} />
         <div className="wd-split wd-split--ring">
            <GoalRing value={today} goal={goal} appeared={appeared} />
            <div className="wd-goal__week">
               <div className="wd-goal__bars" aria-hidden="true">
                  <span className="wd-goal__line" style={{ bottom: `${(goal / max) * 100}%` }} />
                  {week.map((day) => (
                     <span
                        key={day.date}
                        className={`wd-goal__bar${day.answers >= goal ? " is-done" : ""}`}
                        style={{ height: `${appeared ? Math.max(4, (day.answers / max) * 100) : 0}%` }}
                        title={`${formatShortDay(day.date)}: ${day.answers}`}
                     />
                  ))}
               </div>
               <span className="wd-caption">
                  {reached} din 7 zile atinse
               </span>
            </div>
         </div>
      </Tile>
   );
}

/* ================= încercări la examenul teoretic ================= */

function ExamBars({ attempts, active, onActive, appeared }) {
   const [ref, { width, height }] = useElementSize();
   const total = Math.max(1, ...attempts.map((a) => a.total || 0));
   const pass = attempts.find((a) => a.passScore)?.passScore || 0;
   const max = niceMax(total);
   const top = 4;
   const bottom = 14;
   const plotH = Math.max(10, height - top - bottom);
   const slot = attempts.length ? width / attempts.length : 0;
   const barW = Math.max(4, Math.min(24, slot * 0.6));
   const y = (v) => top + plotH - (v / max) * plotH;

   return (
      <div ref={ref} className="wd-bars" onPointerLeave={() => onActive(null)}>
         {width > 0 && height > 0 && (
            <svg width={width} height={height} role="img" aria-label="Scorurile la examenul teoretic">
               {attempts.map((a, i) => {
                  const h = appeared ? (a.score / max) * plotH : 0;
                  const x = i * slot + (slot - barW) / 2;
                  return (
                     <g key={a.id ?? i}>
                        {active === i && <rect className="wd-chart__band" x={i * slot} y={top} width={slot} height={plotH} rx={6} />}
                        <rect
                           className={a.passed ? "wd-chart__ok" : "wd-chart__bad"}
                           x={x}
                           y={top + plotH - h}
                           width={barW}
                           height={h}
                           rx={Math.min(4, barW / 2)}
                        />
                        <rect
                           className="wd-chart__hit"
                           x={i * slot}
                           y={0}
                           width={slot}
                           height={height}
                           onPointerEnter={() => onActive(i)}
                           onClick={() => onActive(i)}
                        />
                     </g>
                  );
               })}
               {pass > 0 && (
                  <>
                     <line className="wd-chart__pass" x1={0} x2={width} y1={y(pass) + 0.5} y2={y(pass) + 0.5} />
                     <text className="wd-chart__tick wd-chart__passLabel" x={2} y={y(pass) - 4}>
                        admis de la {pass}
                     </text>
                  </>
               )}
               <line className="wd-chart__axis" x1={0} x2={width} y1={top + plotH + 0.5} y2={top + plotH + 0.5} />
            </svg>
         )}
      </div>
   );
}

export function ExamTrendWidget({ size, data }) {
   const { theory } = data;
   const [active, setActive] = useState(null);
   const appeared = useAppeared(300);
   const attempts = useMemo(
      () => (theory.history || []).filter((a) => !a.inProgress && a.total > 0),
      [theory.history],
   );
   const last = attempts[attempts.length - 1];
   const best = attempts.reduce((b, a) => (!b || a.score > b.score ? a : b), null);

   if (!theory.loaded) {
      return (
         <Tile id="examTrend" size={size}>
            <Head icon={examIcon} title="Încercări" />
            <Skeleton />
         </Tile>
      );
   }

   if (!last) {
      return (
         <Tile id="examTrend" size={size} to="/student/exam" label="Încercări la examen">
            <Head icon={examIcon} title={size === "1x1" ? "Încercări" : "Încercări la examen"} />
            <div className="wd-next__empty">
               <strong>Încă nicio încercare</strong>
               <span className="wd-caption">Examenul teoretic se dă la școală.</span>
            </div>
         </Tile>
      );
   }

   if (size === "1x1") {
      return (
         <Tile id="examTrend" size={size} to="/student/exam" label={`Ultima încercare: ${last.score} din ${last.total}`}>
            <Head icon={examIcon} title="Încercări" />
            <div className="wd-attempts" aria-hidden="true">
               {attempts.slice(-6).map((a, i) => (
                  <i key={a.id ?? i} className={a.passed ? "is-ok" : "is-bad"} />
               ))}
            </div>
            <div className="wd-big">
               <span className="wd-big__num">{last.score}</span>
               <span className="wd-big__of">/ {last.total}</span>
            </div>
            <span className="wd-caption">ultima · {last.passed ? "admis" : "respins"}</span>
         </Tile>
      );
   }

   const focus = active != null ? attempts[active] : null;
   return (
      <Tile id="examTrend" size={size}>
         <Head
            icon={examIcon}
            title="Încercări la examen"
            extra={
               <span className="wd-legend" aria-hidden="true">
                  <i className="wd-legend__ok" />
                  admis
                  <i className="wd-legend__bad" />
                  respins
               </span>
            }
         />
         <ExamBars attempts={attempts} active={active} onActive={setActive} appeared={appeared} />
         <p className="wd-readout" aria-live="polite">
            {focus
               ? `${formatShortDate(focus.at)}: ${focus.score}/${focus.total} · ${focus.passed ? "admis" : "respins"}`
               : `${roCount(attempts.length, "încercare", "încercări")} · cel mai bun scor: ${best.score}/${best.total}`}
         </p>
      </Tile>
   );
}
